/**
 * Pages for visitors who are signed out. Coming back to one after signing in
 * would only lead to signing in again, so they're never remembered.
 */
const SIGNED_OUT_PAGES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/get-started',
];

export function isSignedOutPage(pathname: string): boolean {
  return SIGNED_OUT_PAGES.some((page) => pathname === page || pathname.startsWith(`${page}/`));
}

/**
 * The page to open after signing in: the one the visitor was sent to sign in
 * from. The value comes from the address bar, so anyone can write a link with
 * it. It's read the way the browser would read it, and dropped unless it's a
 * page of this site.
 */
export function returnPath(redirect: string | undefined, origin: string): string | null {
  if (!redirect?.startsWith('/')) return null;
  let url: URL;
  try {
    url = new URL(redirect, origin);
  } catch {
    return null;
  }
  // A path that normalises to "//host" would be read as another site.
  if (url.origin !== origin || url.pathname.startsWith('//') || isSignedOutPage(url.pathname)) {
    return null;
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The login page, remembering `href`, a page of this site, to come back to. */
export function loginHref(href: string): string {
  const { pathname } = new URL(href, 'https://site.invalid');
  return isSignedOutPage(pathname) ? '/login' : `/login?redirect=${encodeURIComponent(href)}`;
}
